import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import Uppy, { type UppyFile } from "@uppy/core";
import DashboardPlugin from "@uppy/dashboard";
import AwsS3 from "@uppy/aws-s3";
import { BACKEND_URL } from "@/lib/auth";

// Uppy CSS lokal (bundle, kein CDN)
import "@uppy/core/css/style.min.css";
import "@uppy/dashboard/css/style.min.css";

/**
 * HypeFly Uppy Uploader (Uppy 5.x).
 *
 * Emitiert per `onUploadsChange` ein einheitliches UploadItem-Array an den
 * Parent, damit dieser eine prominente Progress-Kartenliste rendern kann
 * (getrennt vom Uppy-eigenen Dashboard, das für große Videos zu subtil ist).
 *
 * Speed + ETA werden über eine EMA per Datei berechnet (Uppy liefert nur
 * bytesUploaded / bytesTotal / percentage — keinen Speed direkt).
 */

const CHUNK_SIZE = 16 * 1024 * 1024; // 16 MB — halbiert Request-Anzahl vs 8 MB (relevant für Edge-Rate-Limits)
const S3_CONCURRENCY = 2; // gleichzeitige Chunk-PUTs pro Datei — begrenzt Request-Burst am Edge

export type UppyUploadItem = {
    id: string;
    name: string;
    size: number;
    bytesUploaded: number;
    percentage: number;
    speed: number; // bytes/sec (EMA)
    etaSec: number;
    status: "queued" | "uploading" | "done" | "error";
    error?: string;
};

export type UppyUploaderHandle = {
    cancel: (fileId: string) => void;
};

type Props = {
    onUploadComplete: () => void;
    onUploadsChange?: (items: UppyUploadItem[]) => void;
    onUploadSuccess?: (item: { id: string; name: string; size: number }) => void;
};

type InitResponse = {
    media_id: string;
    key: string;
    upload_id: string;
    uploadId: string;
    chunk_size: number;
};

type SignPartResponse = {
    url: string;
    presignedUrl: string;
    expires_in: number;
};

/**
 * Kleiner fetch-Wrapper mit exponentiellem Backoff für transiente Fehler
 * (HTTP 429 = rate-limited, 502/503/504 = temporär). Delays passen sich per
 * `opts.aggressive` an: normale Steuer-Calls warten 1s/3s/8s, kritische
 * finalize-Calls (Complete Multipart) warten 5s/15s/30s/60s — die Bytes
 * sind schon bei R2, der User soll nicht neu hochladen müssen.
 *
 * Wichtig: Nur idempotente/POST-Steuer-Aufrufe (init, sign-part, complete,
 * abort) durchlaufen diesen Wrapper. Der eigentliche Chunk-PUT geht direkt
 * an R2 und ist von Uppys eigenem Retry-Handling abgedeckt.
 */
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const RETRY_DELAYS_NORMAL_MS = [1000, 3000, 8000];
const RETRY_DELAYS_AGGRESSIVE_MS = [5000, 15000, 30000, 60000];

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function jfetch<T>(
    url: string,
    body: unknown,
    opts?: { silent?: boolean; aggressive?: boolean },
): Promise<T> {
    const delays = opts?.aggressive ? RETRY_DELAYS_AGGRESSIVE_MS : RETRY_DELAYS_NORMAL_MS;
    const maxRetries = delays.length;
    let lastErr: Error | null = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        let res: Response | null = null;
        try {
            res = await fetch(`${BACKEND_URL}${url}`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
        } catch (netErr) {
            lastErr = netErr instanceof Error ? netErr : new Error(String(netErr));
            if (attempt < maxRetries) {
                await sleep(delays[attempt]);
                continue;
            }
            throw lastErr;
        }
        if (res.ok) return res.json() as Promise<T>;
        if (RETRYABLE_STATUS.has(res.status) && attempt < maxRetries) {
            if (!opts?.silent) {
                console.warn(`[UppyUploader] ${url} → ${res.status}, retry #${attempt + 1} in ${delays[attempt]}ms`);
            }
            await sleep(delays[attempt]);
            continue;
        }
        lastErr = new Error(`${res.status} ${res.statusText}`);
        throw lastErr;
    }
    throw lastErr ?? new Error("Unbekannter Fehler");
}

const UppyUploader = forwardRef<UppyUploaderHandle, Props>(function UppyUploader(
    { onUploadComplete, onUploadsChange, onUploadSuccess },
    ref,
) {
    const mountRef = useRef<HTMLDivElement | null>(null);
    // Map von Uppy-file-id → { media_id, key } für spätere complete-calls
    const contextRef = useRef<Map<string, { media_id: string; key: string }>>(new Map());
    // Speed-Tracking pro File-Id (last bytes + last time + EMA)
    const speedRef = useRef<Map<string, { lastBytes: number; lastT: number; ema: number }>>(new Map());
    // Aktueller UI-State pro File-Id (für Emit an Parent)
    const stateRef = useRef<Map<string, UppyUploadItem>>(new Map());
    // Dedupe-Set für in-flight Abort-Calls. Verhindert, dass ein hektisches
    // remove/cancel-Muster dieselbe abort()-URL parallel mehrfach feuert (was
    // Ingress-seitig als 429-Sturm zurückkommen könnte).
    const abortingRef = useRef<Set<string>>(new Set());
    // Stabile Refs auf Callbacks, damit sie ohne Uppy-Re-Init aktualisiert werden können
    const onUploadsChangeRef = useRef(onUploadsChange);
    const onUploadCompleteRef = useRef(onUploadComplete);
    const onUploadSuccessRef = useRef(onUploadSuccess);
    useEffect(() => { onUploadsChangeRef.current = onUploadsChange; }, [onUploadsChange]);
    useEffect(() => { onUploadCompleteRef.current = onUploadComplete; }, [onUploadComplete]);
    useEffect(() => { onUploadSuccessRef.current = onUploadSuccess; }, [onUploadSuccess]);

    const emit = () => {
        const arr = Array.from(stateRef.current.values());
        onUploadsChangeRef.current?.(arr);
    };

    const upsert = (id: string, patch: Partial<UppyUploadItem>) => {
        const prev = stateRef.current.get(id);
        const next: UppyUploadItem = {
            id,
            name: prev?.name || "",
            size: prev?.size || 0,
            bytesUploaded: prev?.bytesUploaded || 0,
            percentage: prev?.percentage || 0,
            speed: prev?.speed || 0,
            etaSec: prev?.etaSec || 0,
            status: prev?.status || "queued",
            error: prev?.error,
            ...patch,
        };
        stateRef.current.set(id, next);
        emit();
    };

    const uppy = useMemo(() => {
        const u = new Uppy({
            id: "hypefly-uppy",
            autoProceed: false,
            allowMultipleUploadBatches: true,
            restrictions: {
                maxNumberOfFiles: 20,
                allowedFileTypes: ["image/*", "video/*"],
            },
        }).use(AwsS3, {
            id: "AwsS3",
            shouldUseMultipart: true,
            limit: S3_CONCURRENCY,
            retryDelays: [2000, 5000, 15000, 30000, 60000],
            createMultipartUpload: async (file) => {
                const data = await jfetch<InitResponse>("/api/admin/media/multipart/init", {
                    filename: file.name || "upload.bin",
                    content_type: file.type || "application/octet-stream",
                    size: file.size || 0,
                }, { aggressive: true });
                contextRef.current.set(file.id, { media_id: data.media_id, key: data.key });
                return { uploadId: data.uploadId || data.upload_id, key: data.key };
            },
            signPart: async (_file, { uploadId, key, partNumber }) => {
                const data = await jfetch<SignPartResponse>("/api/admin/media/multipart/sign-part", {
                    upload_id: uploadId,
                    key,
                    part_number: partNumber,
                });
                return { method: "PUT", url: data.presignedUrl || data.url };
            },
            listParts: async (_file, { uploadId, key }) => {
                const res = await fetch(
                    `${BACKEND_URL}/api/admin/media/multipart/status?key=${encodeURIComponent(key)}&upload_id=${encodeURIComponent(uploadId ?? "")}`,
                    { credentials: "include" },
                );
                if (!res.ok) return [];
                const data = await res.json();
                return (data?.parts || []) as Array<{ PartNumber: number; Size: number; ETag: string }>;
            },
            completeMultipartUpload: async (file, { uploadId, key, parts }) => {
                // KRITISCH: An diesem Punkt sind alle Bytes bereits bei R2.
                // Ein 429 hier würde bedeuten, dass der User die 138 MB neu
                // hochladen müsste. Deshalb aggressives Backoff: bis zu 4
                // Retries mit 5s/15s/30s/60s Delays (ca. 1min50 max Wartezeit).
                const ctx = contextRef.current.get(file.id);
                await jfetch("/api/admin/media/multipart/complete", {
                    media_id: ctx?.media_id,
                    key,
                    upload_id: uploadId,
                    parts: parts.map((p) => ({ PartNumber: p.PartNumber, ETag: p.ETag })),
                    poster_url: null,
                }, { aggressive: true });
                return { location: undefined };
            },
            abortMultipartUpload: async (_file, { uploadId, key }) => {
                // Abort ist reines Best-Effort-Cleanup (Uppy triggert das,
                // wenn der User remove/cancel drückt oder ein Upload
                // fehlschlägt). Ein Fehler hier DARF unter keinen Umständen
                // die App zum Absturz bringen (rote CRA-Error-Overlay).
                // Deshalb: dedupen, still schlucken, nichts weiterwerfen.
                const dedupeKey = `${key}::${uploadId ?? ""}`;
                if (abortingRef.current.has(dedupeKey)) {
                    return;
                }
                abortingRef.current.add(dedupeKey);
                try {
                    await jfetch("/api/admin/media/multipart/abort", {
                        key,
                        upload_id: uploadId,
                    }, { silent: true });
                } catch (err) {
                    console.warn("[UppyUploader] abort ignored:", (err as Error)?.message);
                } finally {
                    // 30s Fenster gegen Rebounds, danach aufräumen.
                    setTimeout(() => abortingRef.current.delete(dedupeKey), 30_000);
                }
            },
            getChunkSize: () => CHUNK_SIZE,
        });

        // Datei zur Liste hinzufügen
        u.on("file-added", (file: UppyFile<Record<string, unknown>, Record<string, unknown>>) => {
            upsert(file.id, {
                id: file.id,
                name: file.name || "Datei",
                size: file.size || 0,
                status: "queued",
                bytesUploaded: 0,
                percentage: 0,
                speed: 0,
                etaSec: 0,
                error: undefined,
            });
            speedRef.current.set(file.id, { lastBytes: 0, lastT: performance.now(), ema: 0 });
        });

        u.on("file-removed", (file: UppyFile<Record<string, unknown>, Record<string, unknown>>) => {
            stateRef.current.delete(file.id);
            speedRef.current.delete(file.id);
            contextRef.current.delete(file.id);
            emit();
        });

        u.on("upload-progress", (file, progress) => {
            if (!file || !progress) return;
            const bytesUploaded = progress.bytesUploaded || 0;
            const bytesTotal = progress.bytesTotal || file.size || 1;
            const pct = Math.round((bytesUploaded / bytesTotal) * 100);
            const now = performance.now();
            const track = speedRef.current.get(file.id) || { lastBytes: 0, lastT: now, ema: 0 };
            const dt = (now - track.lastT) / 1000;
            let ema = track.ema;
            if (dt > 0.3) {
                const inst = (bytesUploaded - track.lastBytes) / dt;
                ema = ema === 0 ? Math.max(inst, 0) : ema * 0.65 + Math.max(inst, 0) * 0.35;
                speedRef.current.set(file.id, { lastBytes: bytesUploaded, lastT: now, ema });
            }
            const remaining = ema > 0 ? Math.max(0, (bytesTotal - bytesUploaded) / ema) : 0;
            upsert(file.id, {
                bytesUploaded,
                percentage: pct,
                speed: ema,
                etaSec: remaining,
                status: "uploading",
            });
        });

        u.on("upload-success", (file) => {
            if (!file) return;
            upsert(file.id, {
                bytesUploaded: file.size || 0,
                percentage: 100,
                speed: 0,
                etaSec: 0,
                status: "done",
            });
            onUploadSuccessRef.current?.({
                id: file.id,
                name: file.name || "Datei",
                size: file.size || 0,
            });
        });

        u.on("upload-error", (file, error) => {
            if (!file) return;
            const msg = (error as Error | undefined)?.message || "Upload fehlgeschlagen";
            console.warn("[UppyUploader] upload-error", file?.name, msg);
            upsert(file.id, { status: "error", error: msg });
        });

        u.on("cancel-all", () => {
            stateRef.current.clear();
            speedRef.current.clear();
            emit();
        });

        u.on("complete", () => {
            onUploadCompleteRef.current();
        });

        return u;
    }, []);

    // Cancel via Ref (Parent kann per Klick auf die Karte abbrechen)
    useImperativeHandle(ref, () => ({
        cancel: (fileId: string) => {
            try {
                const files = uppy.getFiles();
                const exists = files.some((f) => f.id === fileId);
                if (exists) uppy.removeFile(fileId);
            } catch {
                /* ignore */
            }
        },
    }), [uppy]);

    // Dashboard-Plugin an mountRef koppeln
    useEffect(() => {
        if (!mountRef.current) return;
        uppy.use(DashboardPlugin, {
            id: "Dashboard",
            target: mountRef.current,
            inline: true,
            proudlyDisplayPoweredByUppy: false,
            hideProgressDetails: false,
            hideUploadButton: false,
            hideCancelButton: false,
            hidePauseResumeButton: false,
            height: 440,
            width: "100%",
            theme: "light",
            note: "Fotos & Videos (JPG, PNG, WebP, MP4, MOV, WebM). Große Videos (bis mehrere GB) werden per Chunk-Upload zuverlässig übertragen und automatisch für Web optimiert. Der aktuelle Status ist unten in den Fortschritts-Karten sichtbar. Insta360-Rohdateien (.insv) bitte vorher als equirektangulares MP4 exportieren.",
            locale: {
                strings: {
                    dropPasteFiles: "Dateien hierhin ziehen oder %{browseFiles}",
                    browseFiles: "auswählen",
                    uploadingXFiles: {
                        0: "%{smart_count} Datei wird hochgeladen",
                        1: "%{smart_count} Dateien werden hochgeladen",
                    },
                    cancel: "Abbrechen",
                    retry: "Erneut versuchen",
                    removeFile: "Datei entfernen",
                    done: "Fertig",
                    uploadComplete: "Upload abgeschlossen",
                    uploadFailed: "Upload fehlgeschlagen",
                    pause: "Pause",
                    resume: "Fortsetzen",
                    upload: "Hochladen",
                    addMore: "Weitere hinzufügen",
                    addMoreFiles: "Weitere Dateien",
                    editing: "Bearbeiten",
                },
            },
        });
        return () => {
            try {
                const plugin = uppy.getPlugin("Dashboard");
                if (plugin) uppy.removePlugin(plugin);
            } catch {
                /* ignore */
            }
        };
    }, [uppy]);

    useEffect(() => {
        return () => {
            try {
                uppy.destroy();
            } catch {
                /* ignore */
            }
        };
    }, [uppy]);

    return <div className="hf-uppy-wrapper" data-testid="uppy-uploader" ref={mountRef} />;
});

export default UppyUploader;
